import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useListApiKeys,
  useCreateApiKey,
  useRevokeApiKey,
  getListApiKeysQueryKey,
} from "@workspace/api-client-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, KeyRound, Copy, Ban } from "lucide-react";

function formatDate(value: string | null): string {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export default function ApiKeysPage() {
  const qc = useQueryClient();
  const listQuery = useListApiKeys();
  const apiKeys = listQuery.data?.apiKeys ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState<number | null>(null);

  const refetch = () =>
    qc.invalidateQueries({ queryKey: getListApiKeysQueryKey() });

  const revokeMutation = useRevokeApiKey({
    mutation: {
      onSuccess: () => {
        toast.success("API key revoked");
        setConfirmRevokeId(null);
        void refetch();
      },
      onError: (err: Error) => toast.error(err.message),
    },
  });

  const activeKeys = apiKeys.filter((k) => k.revokedAt === null);
  const revokedKeys = apiKeys.filter((k) => k.revokedAt !== null);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="api-keys-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">API keys</h1>
          <p className="text-sm text-muted-foreground">
            Server-side keys used by the official{" "}
            <code className="font-mono">@mailcraft/sdk</code> (JS) and{" "}
            <code className="font-mono">mailcraft</code> (Python) packages
            to authenticate calls to{" "}
            <code className="font-mono">/send</code>,{" "}
            <code className="font-mono">/templates</code>, and{" "}
            <code className="font-mono">/contacts</code>. Each key is sent
            as <code className="font-mono">Authorization: Bearer mc_…</code>.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="button-new-api-key">
          <Plus className="mr-2 h-4 w-4" /> New API key
        </Button>
      </div>

      {listQuery.isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : activeKeys.length === 0 && revokedKeys.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No API keys yet. Click <strong>New API key</strong> to mint one.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {activeKeys.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Active
              </h2>
              {activeKeys.map((k) => (
                <Card key={k.id} data-testid={`api-key-row-${k.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-base flex items-center gap-2">
                          <KeyRound className="h-4 w-4 text-muted-foreground" />
                          <span className="truncate" data-testid={`api-key-label-${k.id}`}>
                            {k.label}
                          </span>
                        </CardTitle>
                        <CardDescription className="mt-1 font-mono text-xs">
                          {k.secretPrefix}…
                        </CardDescription>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmRevokeId(k.id)}
                        data-testid={`button-revoke-${k.id}`}
                      >
                        <Ban className="mr-1.5 h-4 w-4" /> Revoke
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 text-xs text-muted-foreground grid grid-cols-2 gap-x-6 gap-y-1">
                    <div>
                      <span className="font-medium">Created:</span>{" "}
                      {formatDate(k.createdAt)}
                    </div>
                    <div>
                      <span className="font-medium">Last used:</span>{" "}
                      {formatDate(k.lastUsedAt)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </section>
          )}

          {revokedKeys.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Revoked
              </h2>
              {revokedKeys.map((k) => (
                <Card key={k.id} className="opacity-70" data-testid={`api-key-row-${k.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-base flex items-center gap-2 line-through">
                          <KeyRound className="h-4 w-4 text-muted-foreground" />
                          <span className="truncate">{k.label}</span>
                        </CardTitle>
                        <CardDescription className="mt-1 font-mono text-xs">
                          {k.secretPrefix}…
                        </CardDescription>
                      </div>
                      <Badge variant="outline">Revoked</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 text-xs text-muted-foreground grid grid-cols-2 gap-x-6 gap-y-1">
                    <div>
                      <span className="font-medium">Created:</span>{" "}
                      {formatDate(k.createdAt)}
                    </div>
                    <div>
                      <span className="font-medium">Revoked:</span>{" "}
                      {formatDate(k.revokedAt)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </section>
          )}
        </div>
      )}

      <CreateApiKeyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(secret) => {
          setRevealedSecret(secret);
          void refetch();
        }}
      />

      <RevealSecretDialog
        secret={revealedSecret}
        onClose={() => setRevealedSecret(null)}
      />

      <AlertDialog
        open={confirmRevokeId !== null}
        onOpenChange={(open) => !open && setConfirmRevokeId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this API key?</AlertDialogTitle>
            <AlertDialogDescription>
              Any application currently using this key will start receiving
              <strong> 401 Unauthorized</strong> immediately. You cannot undo
              this — mint a new key if you need to rotate.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-revoke"
              onClick={() => {
                if (confirmRevokeId !== null) {
                  revokeMutation.mutate({ id: confirmRevokeId });
                }
              }}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateApiKeyDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (secret: string) => void;
}) {
  const [label, setLabel] = useState("");

  const createMutation = useCreateApiKey({
    mutation: {
      onSuccess: (data) => {
        toast.success("API key created");
        setLabel("");
        onOpenChange(false);
        onCreated(data.secret);
      },
      onError: (err: Error) => toast.error(err.message),
    },
  });

  function submit() {
    const trimmed = label.trim();
    if (!trimmed) {
      toast.error("Label is required");
      return;
    }
    createMutation.mutate({ data: { label: trimmed } });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New API key</DialogTitle>
          <DialogDescription>
            Pick a short label so you can identify this key later
            (e.g. "Production backend"). The full secret is shown exactly
            once after creation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="api-key-label">Label</Label>
            <Input
              id="api-key-label"
              data-testid="input-api-key-label"
              placeholder="Production backend"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={80}
              autoFocus
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={createMutation.isPending}
            data-testid="button-create-api-key"
          >
            {createMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevealSecretDialog({
  secret,
  onClose,
}: {
  secret: string | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={secret !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save your API key</DialogTitle>
          <DialogDescription>
            This key is shown <strong>exactly once</strong>. Store it
            somewhere safe — you will not be able to retrieve it again.
            Pass it to the SDK as{" "}
            <code className="font-mono">apiKey</code> or set it as the{" "}
            <code className="font-mono">MAILCRAFT_API_KEY</code> environment
            variable.
          </DialogDescription>
        </DialogHeader>
        <div
          className="rounded-md border bg-muted/40 p-3 font-mono text-sm break-all"
          data-testid="text-revealed-secret"
        >
          {secret}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              if (secret) {
                void navigator.clipboard.writeText(secret);
                toast.success("Secret copied to clipboard");
              }
            }}
          >
            <Copy className="mr-2 h-4 w-4" /> Copy
          </Button>
          <Button onClick={onClose}>I have saved it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
